// POPULATION — marriage + education orchestration. Listens to UI requests,
// validates affordability + prerequisites, mutates state via seasonManager.

import { Events, emit, on } from '../ui/eventBus.js'
import {
  GoonTypes,
  LineageConfig,
  GoonTrainingConfig as EducationConfig,
  LicenseTypes,
} from '../config/balance.js'
import { state, activeId, persist } from './state.js'
import * as season from './seasonManager.js'
import { hasLicense } from './profileManager.js'

export function init() {
  on(Events.MarriageRequested, ({ recipe }) => marry(recipe))
  on(Events.EducationRequested, ({ goonId }) => educate(goonId))
  on(Events.MigrationRequested, ({ goonId, toKingdom }) => migrate(goonId, toKingdom))
}

// Cross-kingdom move: 1 goon shifts from active kingdom to `toKingdom`.
// Direct state mutation since this touches a non-active kingdom slice that
// seasonManager's getters don't reach.
function migrate(goonId, toKingdom) {
  const fromId = activeId()
  if (fromId === toKingdom) return false
  const from = state.kingdoms[fromId]
  const to = state.kingdoms[toKingdom]
  if (!from || !to || !to.unlocked) return false
  if ((from.goons?.[goonId] ?? 0) < 1) return false
  from.goons[goonId] = (from.goons[goonId] ?? 0) - 1
  to.goons[goonId] = (to.goons[goonId] ?? 0) + 1
  persist()
  // The source is the active kingdom — emit so its peasants/HUD update.
  emit(Events.GoonOwnedChange, { goonId, owned: from.goons[goonId] })
  return true
}

export function marriageRecipes() {
  return LineageConfig
}

export function canMarry(recipe) {
  return (
    hasLicense(LicenseTypes.MARRIAGE) &&
    season.getGoonsOwned(recipe.from) >= recipe.requires &&
    season.getWealth() >= recipe.cost
  )
}

export function marriageBlockReason(recipe) {
  if (!hasLicense(LicenseTypes.MARRIAGE)) return 'NEED MARRIAGE LICENSE'
  if (season.getGoonsOwned(recipe.from) < recipe.requires) return null
  if (season.getWealth() < recipe.cost) return null
  return null
}

function marry(recipe) {
  if (!canMarry(recipe)) return false
  if (!season.spendWealth(recipe.cost)) return false
  season.adjustGoonsOwned(recipe.from, -recipe.requires, `joined a lineage (became ${recipe.produces})`)
  season.adjustGoonsOwned(recipe.produces, 1)
  return true
}

export function educationCost(goonId) {
  const level = season.getEducationLevel(goonId)
  if (level >= EducationConfig.maxLevel) return Infinity
  return Math.ceil(EducationConfig.baseCost * Math.pow(EducationConfig.costGrowth, level))
}

export function educationBonus(goonId) {
  return 1 + EducationConfig.levelBonus * season.getEducationLevel(goonId)
}

export function canEducate(goonId) {
  return (
    season.getEducationLevel(goonId) < EducationConfig.maxLevel &&
    season.getWealth() >= educationCost(goonId)
  )
}

function educate(goonId) {
  if (!canEducate(goonId)) return false
  if (!season.spendWealth(educationCost(goonId))) return false
  season.incrementEducationLevel(goonId)
  return true
}

// Helper for goonManager: applies per-goon-type education multiplier when
// computing effective click rates.
export function goonClickRateMultiplier(goonId) {
  return educationBonus(goonId)
}

// Educatable types: all goons. Filter callers to non-noble if they want to
// hide marriage-only types from the upgrade UI.
export function educatableTypes() {
  return Object.values(GoonTypes)
}
